import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

/* Componente único e reutilizável de confirmação, usado em toda a app antes de qualquer
   ação de apagar (requisito: "um único componente reutilizável para toda a app, em vez de
   diálogos diferentes em cada página"). Nunca confirma por Enter nem ao clicar fora — o
   botão "Cancelar" é o foco por defeito, e fechar o diálogo de qualquer forma (Escape,
   clique fora, X) resolve sempre para "cancelado". */

const ConfirmDeleteContext = createContext(null);
const ConfirmClearContext = createContext(null);

export function ConfirmDialogsProvider({ children }) {
  const [deleteState, setDeleteState] = useState(null); // { description, linked, confirmLabel, resolve }
  const [clearState, setClearState] = useState(null); // { fieldLabel, resolve }
  const deleteCancelRef = useRef(null);
  const clearCancelRef = useRef(null);

  useEffect(() => { if (deleteState) deleteCancelRef.current?.focus(); }, [deleteState]);
  useEffect(() => { if (clearState) clearCancelRef.current?.focus(); }, [clearState]);

  const confirmDelete = useCallback(({ description, linked, confirmLabel } = {}) => {
    return new Promise((resolve) => {
      setDeleteState({ description, linked: linked || [], confirmLabel: confirmLabel || 'Apagar', resolve });
    });
  }, []);

  const confirmClear = useCallback((fieldLabel) => {
    return new Promise((resolve) => {
      setClearState({ fieldLabel, resolve });
    });
  }, []);

  const closeDelete = (result) => {
    deleteState?.resolve?.(result);
    setDeleteState(null);
  };
  const closeClear = (result) => {
    clearState?.resolve?.(result);
    setClearState(null);
  };

  return (
    <ConfirmDeleteContext.Provider value={confirmDelete}>
      <ConfirmClearContext.Provider value={confirmClear}>
        {children}

        {/* Diálogo de eliminação — único em toda a app */}
        <Dialog open={!!deleteState} onOpenChange={(open) => { if (!open) closeDelete(false); }}>
          <DialogContent className="rounded-3xl border-brand-border max-w-sm" data-testid="confirm-delete-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-brand-text">Tem a certeza?</DialogTitle>
              <DialogDescription className="text-sm text-brand-muted pt-1" data-testid="confirm-delete-description">
                {deleteState?.description}
                {deleteState?.linked?.length > 0 && (
                  <span className="block mt-2 text-amber-600 text-xs" data-testid="confirm-delete-linked-info">
                    Este registo tem dados ligados: {deleteState.linked.join(', ')}.
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                ref={deleteCancelRef}
                onClick={() => closeDelete(false)}
                className="flex-1 bg-brand-bg border border-brand-border text-brand-text rounded-full py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-green"
                data-testid="confirm-delete-cancel-btn"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => closeDelete(true)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full py-2.5 text-sm font-medium"
                data-testid="confirm-delete-confirm-btn"
              >
                {deleteState?.confirmLabel || 'Apagar'}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Diálogo de aviso ao esvaziar um campo de texto — único em toda a app */}
        <Dialog open={!!clearState} onOpenChange={(open) => { if (!open) closeClear(false); }}>
          <DialogContent className="rounded-3xl border-brand-border max-w-sm" data-testid="confirm-clear-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-brand-text" data-testid="confirm-clear-title">
                Vais apagar o conteúdo de {clearState?.fieldLabel}. Tens a certeza?
              </DialogTitle>
            </DialogHeader>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                ref={clearCancelRef}
                onClick={() => closeClear(false)}
                className="flex-1 bg-brand-bg border border-brand-border text-brand-text rounded-full py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-green"
                data-testid="confirm-clear-cancel-btn"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => closeClear(true)}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full py-2.5 text-sm font-medium"
                data-testid="confirm-clear-confirm-btn"
              >
                Apagar conteúdo
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </ConfirmClearContext.Provider>
    </ConfirmDeleteContext.Provider>
  );
}

/* confirmDelete({ description, linked, confirmLabel }) => Promise<boolean> */
export function useConfirmDelete() {
  const ctx = useContext(ConfirmDeleteContext);
  if (!ctx) throw new Error('useConfirmDelete tem de ser usado dentro de ConfirmDialogsProvider');
  return ctx;
}

/* confirmClear(fieldLabel) => Promise<boolean> */
export function useConfirmClear() {
  const ctx = useContext(ConfirmClearContext);
  if (!ctx) throw new Error('useConfirmClear tem de ser usado dentro de ConfirmDialogsProvider');
  return ctx;
}
